import { FeaturesPopupData } from "api/interfaces/settingsInterface.interface";
import BigButton from "components/atoms/buttons/BigButton";
import TableButton from "components/atoms/buttons/TableButton";
import Input from "components/atoms/input/Input";
import { ModalInput } from "components/atoms/input/modal/ModalInput";
import BigPopupTemplate from "components/templates/modal/big/BigPopupTemplate";
import * as s from "../FeaturesPopupStyled";
import { ReactComponent as PlusCircleIcon } from "assets/icons/plus_in_circle.svg";
import { FaMinusCircle } from "react-icons/fa";
import { ValidateWord } from "components/atoms/text/Text";
import { ValidateMsg } from "api/interfaces/commonInterface.interface";
import Toggle from "components/atoms/toggle/Toggle";
import InputDate from "components/atoms/calendar/InputDate";
import DeleteConfirmModal from "components/templates/modal/small/confirm/delete/DeleteConfirmModal";
import Spinner from "components/atoms/loader/Spinner";
import moment from "moment";
import TextArea from "components/atoms/input/TextArea";

type Props = {
  data: FeaturesPopupData;
  onChangeFeatures: (key: string, value: string | boolean | Date) => void;
  onChangeSlides: (index: number, key: string, value: string) => void;
  onClose: () => void;
  onClickAddSlide: () => void;
  onClickPreview: () => void;
  onClickSave: () => void;
  onChangeImage: (id: string, file: File) => void;
  onDeleteSlide: (id: string) => void;
  onClickDelete: () => void;
  validateMsg: ValidateMsg;
  showDeleteConfirmModal: boolean;
  onConfirmDelete: () => void;
  deleteDisabled: boolean;
  disabledSave: boolean;
};
export default function AddFeaturesPopupPresenter(props: Props) {
  return (
    <BigPopupTemplate
      title={props.data.id ? "Edit Announcement" : "Add Announcement"}
      onModal={props.onClose}
    >
      <ModalInput
        label="Title"
        essential
        isValidated={props.validateMsg.title ? true : false}
      >
        <Input
          value={props.data.title}
          placeholder="Enter pop-up title"
          onChange={(e) => props.onChangeFeatures("title", e.target.value)}
          data-testid="manager-features-add-input-change-features"
        />

        {props.validateMsg.title && (
          <ValidateWord data-testid="manager-features-add-validate-title">
            {props.validateMsg.title}
          </ValidateWord>
        )}
      </ModalInput>
      {props.data.id && (
        <ModalInput
          label="Display Pop-up"
          isValidated={props.validateMsg.isActive ? true : false}
        >
          <Toggle
            isToggled={props.data.isActive}
            onChange={(checked) => props.onChangeFeatures("isActive", checked)}
            dataTestId="manager-features-add-display-popup-toggle"
          />
        </ModalInput>
      )}

      <s.AddFeatures.ScheduleDisplayPeriodItem>
        <ModalInput
          label="Schedule Display"
          isValidated={
            props.validateMsg.startDate || props.validateMsg.endDate
              ? true
              : false
          }
          essential
        >
          <InputDate
            mode="range"
            value={[props.data.startDate, props.data.endDate]}
            onChange={(date) => {
              props.onChangeFeatures("startDate", date?.[0]);
              props.onChangeFeatures("endDate", date?.[1]);
            }}
            timezone="America/Detroit"
            required
          />

          {(props.validateMsg.startDate || props.validateMsg.endDate) && (
            <ValidateWord data-testid="manager-features-add-validate-start-date">
              {props.validateMsg.startDate}
              {props.validateMsg.endDate}
            </ValidateWord>
          )}

          <s.AddFeatures.TimezoneText>
            {moment.tz("America/Detroit").format("z")}
          </s.AddFeatures.TimezoneText>
        </ModalInput>
      </s.AddFeatures.ScheduleDisplayPeriodItem>
      <ModalInput
        label="Slides"
        isValidated={props.validateMsg.slides ? true : false}
      >
        {props.validateMsg.slides && (
          <ValidateWord data-testid="manager-features-add-validate-slides">
            {props.validateMsg.slides}
          </ValidateWord>
        )}

        <s.AddFeatures.SlideList>
          {props.data.slides.map((slide, index) => (
            <s.AddFeatures.SlideItem key={index}>
              <s.AddFeatures.SlideItemTitle>
                <s.AddFeatures.DeleteButton
                  onClick={() => props.onDeleteSlide(slide.id as string)}
                  data-testid="manager-features-add-delete-button-delete-slide"
                >
                  <FaMinusCircle size={16} />
                </s.AddFeatures.DeleteButton>
                Slide {index + 1}
              </s.AddFeatures.SlideItemTitle>
              <s.AddFeatures.SlideItemInput>
                <Input
                  value={slide.title}
                  isValidated={
                    props.validateMsg.slides && !slide.title ? true : false
                  }
                  placeholder={`Slide ${index + 1} title`}
                  onChange={(e) =>
                    props.onChangeSlides(index, "title", e.target.value)
                  }
                  data-testid="manager-features-add-input-change-slides"
                />
              </s.AddFeatures.SlideItemInput>
              <s.AddFeatures.SlideItemInput>
                <Input
                  readOnly
                  value={slide.fileName}
                  placeholder="Upload image"
                  isValidated={
                    props.validateMsg.slides && !slide.url ? true : false
                  }
                />

                <TableButton label="Upload" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      props.onChangeImage(slide.id as string, file);
                    }
                  }}
                  data-testid="manager-features-add-input-change-image"
                />
              </s.AddFeatures.SlideItemInput>
              <s.AddFeatures.SlideItemInput className="description">
                <TextArea
                  value={slide.description}
                  isValidated={
                    props.validateMsg.slides && !slide.description
                      ? true
                      : false
                  }
                  placeholder={`Slide ${index + 1} description`}
                  onChange={(e) =>
                    props.onChangeSlides(index, "description", e.target.value)
                  }
                  maxLength={500}
                  data-testid="manager-features-add-textarea-change-slides"
                />

                <span>
                  <b>{slide.description?.length || 0}</b>/500
                </span>
              </s.AddFeatures.SlideItemInput>
            </s.AddFeatures.SlideItem>
          ))}
        </s.AddFeatures.SlideList>
        <s.AddFeatures.AddSlideButton>
          <TableButton
            label="Add Slide"
            buttonType="primary"
            icon={<PlusCircleIcon />}
            onClick={props.onClickAddSlide}
            data-testid="manager-features-add-table-button-click-add-slide"
          />
        </s.AddFeatures.AddSlideButton>
      </ModalInput>
      <s.AddFeatures.ButtonWrapper>
        {props.data.id && (
          <BigButton
            label="Delete"
            buttonType="warning"
            onClickBtn={props.onClickDelete}
            disabled={props.deleteDisabled}
            data-testid="manager-features-add-button-click-delete"
          />
        )}

        <BigButton
          label="Preview"
          buttonType="cancel"
          onClickBtn={props.onClickPreview}
          data-testid="manager-features-add-button-click-preview"
        />

        <BigButton
          label="Save"
          onClickBtn={props.onClickSave}
          disabled={props.disabledSave}
          data-testid="manager-features-add-button-click-save"
        />
      </s.AddFeatures.ButtonWrapper>
      {props.showDeleteConfirmModal && (
        <DeleteConfirmModal
          label="Announcement"
          onDelete={props.onClickDelete}
          onConfirmDelete={props.onConfirmDelete}
        />
      )}

      {props.disabledSave && <Spinner isCenter={true} />}
    </BigPopupTemplate>
  );
}
